import React, { useEffect, useState } from 'react';
import { Button, Card, Dropdown, DropdownButton, Form, Modal, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { IPassword, IUser } from '../../../model/user/IUser';
import { UserService } from '../UserService';
import { UserFilter } from './UserFilter';
import { useNavigate } from 'react-router';
import { ErrorToast, SuccessToast, WarningToast } from '../../../utils/toasters';
import { formatDate, hasAccessRight } from '../../../utils/Functions';
import { AdminAclEnum } from '../../../enum/AdminAclEnum';
import { ImpersonateEye } from '../../../shared/components/ImpersonateEye';
import { PasswordVerifySteps } from '../../../shared/components/PasswordVerifySteps';
import { AiFillEye, AiFillEyeInvisible } from 'react-icons/ai';
import { ImMobile } from 'react-icons/im';
import Flag from '../../../shared/components/Flag';
import { useDispatch } from 'react-redux';
import generalSlice from '../../../store/general/GeneralSlice';
import TableLayout from '../../../shared/layout/table/TableLayout';
import { ITableColumn } from '../../../shared/components/TableSkeleton';
import ToggleChip from '../../../shared/components/ToggleChip';

const USER_COLUMNS: ITableColumn[] = [
    { label: 'ID' },
    { label: 'Role' },
    { label: 'Display name' },
    { label: 'Country' },
    { label: 'Email' },
    { label: 'Last login' },
    { label: 'Is Supervisor?' },
    { label: 'Action' },
];

const initialState = {
    page: 1,
    perPage: 15,
    displayName: '',
    email: '',
    firstName: '',
    lastName: '',
    type: '',
    totalPages: 0,
};

const FILTER_KEYS = [
    'id',
    'search',
    'userType',
    'isEnabled',
    'isSuspended',
    'isSupervisor',
    'country',
    'isOnboarded',
    'gender',
    'campaignCategory',
    'agencyCompanyId',
    'isIvConnectedClub',
    'hasMobileLogin',
];

const UserList = () => {
    const [dataLoading, setDataLoading] = useState<boolean>(true);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [passState, setPassState] = useState<IPassword>({
        password: '',
        repeatPassword: '',
    });
    const [state, setState] = useState(initialState);
    const [selectedUser, setSelectedUser] = useState<IUser | null>();
    const [user, setUser] = useState<IUser[]>([]);
    const [change, setChange] = useState(false);
    const [isPasswordHidden, setIsPasswordHidden] = useState(false);
    const [resetKey, setResetKey] = useState(0);

    useEffect(() => {
        setDataLoading(true);
        UserService.getUsers(state)
            .then(data => {
                setUser(data.data);
                setState(prev => ({ ...prev, totalPages: Math.ceil(data.count / state.perPage) }));
            })
            .catch(err => ErrorToast(err))
            .finally(() => setDataLoading(false));
    }, [change]);

    function disableUsers(id: number) {
        UserService.disableUser(id)
            .then(data => {
                const updatedUsers = user.map(prev => {
                    if (prev.id === id) return data;
                    return prev;
                });
                setUser(updatedUsers);
                const status = !!data.isEnabled ? 'enabled' : 'disabled';
                SuccessToast('Successfully ' + status + ' user');
                dispatch(
                    generalSlice.actions.setConfirmModal({
                        show: false,
                    })
                );
            })
            .catch(err => ErrorToast(err));
    }

    // const toggleOnboarding = (id: number) => {
    //     UserService.onboardUser(id)
    //         .then((data) => {
    //             const updatedUsers = user.map((prev) => {
    //                 if(prev?.id! === id) return data
    //                 return prev
    //             })
    //             setUser(updatedUsers);
    //             SuccessToast('Successful action')
    //         })
    //         .catch((err) => ErrorToast(err));
    // }

    function suspendUser(id: number) {
        UserService.suspendUser(id)
            .then(data => {
                const updatedUsers = user.map(prev => {
                    if (prev?.id === id) return data;
                    return prev;
                });
                setUser(updatedUsers);
                SuccessToast('Successful action');
                dispatch(
                    generalSlice.actions.setConfirmModal({
                        show: false,
                    })
                );
            })
            .catch(err => ErrorToast(err));
    }

    function passData(route: string, data?: any) {
        navigate(route, {
            state: data,
        });
    }

    const handlePages = (updatePage: number) => {
        setState({ ...state, page: updatePage });
        setChange(prev => !prev);
    };

    function handleClick() {
        setDataLoading(true);
        setState(prev => {
            return { ...prev, page: 1 };
        });
        setChange(prev => !prev);
        setDataLoading(false);
    }

    function handleClear() {
        setDataLoading(true);
        setState(initialState);
        setResetKey(prev => prev + 1);
        setChange(prev => !prev);
        setDataLoading(false);
    }

    const hasActiveFilters = FILTER_KEYS.some(key => {
        const value = (state as any)[key];
        return value !== undefined && value !== null && value !== '';
    });

    function handleChange(
        evt: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
        isSelectBoolean?: boolean
    ) {
        const { name, value } = evt.target;
        let newValue: any = value?.replace(/^@/, '');
        if (isSelectBoolean) {
            if (value === 'null') {
                newValue = undefined;
            } else newValue = value === 'enable';
        }
        setState({
            ...state,
            [name]: typeof newValue === 'string' ? newValue?.trim() : newValue, // remove @ on first place
        });
    }

    const changePasswordHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setPassState(prev => ({ ...prev, [name]: value }));
    };

    const handleCloseModal = () => {
        setShowChangePassword(false);
        setSelectedUser(null);
    };

    const submitPasswordHandler = async () => {
        if (passState?.password === passState?.repeatPassword) {
            await UserService.changePassword(selectedUser?.id as number, passState)
                .then(response => {
                    if (response?.id) {
                        SuccessToast(`You have successfully changed password for user ID ${response?.id}`);
                        handleCloseModal();
                    }
                })
                .catch(error => ErrorToast(error));
        } else {
            WarningToast('Provided passwords are not the same');
        }
    };

    const editSupervisor = (id: number) => {
        UserService.updateSupervisor(id)
            .then(data => {
                setUser(prev => prev.map(item => (item?.id === id ? data : item)));
                SuccessToast('Successfully action');
            })
            .catch(err => ErrorToast(err));
    };

    return (
        <>
            <h3 className="text-dark fw-bold fs-2 mb-4">Customer List</h3>

            <Card
                className="mb-5 p-4"
                onKeyDown={e => {
                    if (e.key === 'Enter') handleClick();
                }}
            >
                <UserFilter
                    key={resetKey}
                    handleChanges={handleChange}
                    handleClicks={handleClick}
                    allUsers={true}
                    setState={setState}
                    onClear={handleClear}
                    hasActiveFilters={hasActiveFilters}
                />
            </Card>

            <TableLayout
                pagination={state}
                listLength={user.length}
                handlePages={handlePages}
                isLoading={dataLoading}
                dataName={'users'}
                skeletonColumns={USER_COLUMNS}
                skeletonRows={state.perPage}
            >
                <Table hover responsive>
                    <thead>
                        <tr className="even">
                            {USER_COLUMNS.map(column => (
                                <th key={column.label} className={column.className}>
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {user?.map(e => (
                            <tr key={e.id}>
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip id={`tooltip-${e.id}`}>
                                            {!e.isEnabled ? 'Disabled' : e.isSuspended ? 'Suspended' : 'Active'}
                                        </Tooltip>
                                    }
                                >
                                    <td className={!e.isEnabled ? 'text-danger' : e.isSuspended ? 'text-warning' : ''}>
                                        {e.id}
                                    </td>
                                </OverlayTrigger>
                                <td>{e.userType ?? '-'}</td>
                                <td>
                                    {e.displayName && (
                                        <>
                                            {e.displayName}
                                            <br />
                                        </>
                                    )}
                                    {e.firstName ?? '-'} {e.lastName}
                                </td>
                                <td>
                                    <Flag code={e?.contact?.country} />
                                </td>
                                <td>
                                    <ImpersonateEye id={e.id ?? 0} isEnabled={e.isEnabled} />
                                    {e.email}
                                </td>
                                <td>
                                    {formatDate(e.lastLogin as string) ?? '-'}
                                    <br /> IP:{e.lastLoginIp ?? '-'}
                                    {e.hasMobileLogin && (
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={
                                                <Tooltip id="impressions-tooltip">
                                                    {e?.mobileDate && formatDate(e?.mobileDate)}
                                                </Tooltip>
                                            }
                                        >
                                            <ImMobile size={18} className="cursor-pointer ml-1" />
                                        </OverlayTrigger>
                                    )}
                                </td>
                                <td>
                                    {e?.userType === 'brand' || e?.userType === 'brand_master' ? (
                                        <ToggleChip
                                            name="isSupervisor"
                                            onChange={() => editSupervisor(e?.id)}
                                            checked={!!e.isSupervisor}
                                            label={e.isSupervisor ? 'Yes' : 'No'}
                                        />
                                    ) : (
                                        '-'
                                    )}
                                </td>
                                <td>
                                    <DropdownButton title="Actions" id="dropdown-menu-align-right">
                                        <Dropdown.Item eventKey="1" onClick={() => passData(`/user/edit/${e.id}/true`)}>
                                            Edit
                                        </Dropdown.Item>
                                        {hasAccessRight(AdminAclEnum.USER_ADVANCED_EDIT) && (
                                            <Dropdown.Item
                                                eventKey="1a"
                                                onClick={() => passData(`/user/advanced-edit/${e.id}`)}
                                            >
                                                Advanced edit
                                            </Dropdown.Item>
                                        )}
                                        {['influencer', 'brand_master', 'brand'].includes(e.userType) && (
                                            <Dropdown.Item
                                                eventKey="2"
                                                onClick={() => passData(`/user/devices/${e.id}`, { user: e })}
                                            >
                                                Devices
                                            </Dropdown.Item>
                                        )}
                                        {e.userType === 'influencer' && (
                                            <Dropdown.Item
                                                eventKey="2"
                                                onClick={() => passData('/user/platforms', { id: e.id, user: e })}
                                            >
                                                Edit platforms
                                            </Dropdown.Item>
                                        )}
                                        <Dropdown.Item
                                            eventKey="3"
                                            onClick={() => {
                                                setShowChangePassword(true);
                                                setSelectedUser(e);
                                            }}
                                        >
                                            Change password
                                        </Dropdown.Item>
                                        <Dropdown.Divider />
                                        <Dropdown.Item
                                            onClick={() => {
                                                dispatch(
                                                    generalSlice.actions.setConfirmModal({
                                                        show: true,
                                                        title: 'User status change',
                                                        contentBody: (
                                                            <div>
                                                                Are you sure you want to{' '}
                                                                {e.isEnabled ? 'disable' : 'enable'} this{' '}
                                                                <span className="text-primary">
                                                                    {e?.firstName} {e?.lastName}
                                                                </span>{' '}
                                                                user?
                                                            </div>
                                                        ),
                                                        action: () => disableUsers(e.id ?? 0),
                                                    })
                                                );
                                            }}
                                            eventKey="3"
                                        >
                                            {e.isEnabled ? 'Disable' : 'Enable'}
                                        </Dropdown.Item>
                                        <Dropdown.Divider />
                                        <Dropdown.Item
                                            onClick={() => {
                                                dispatch(
                                                    generalSlice.actions.setConfirmModal({
                                                        show: true,
                                                        title: 'User status change',
                                                        contentBody: (
                                                            <div>
                                                                Are you sure you want to{' '}
                                                                {e.isSuspended ? 'lift suspension' : 'suspend'} this{' '}
                                                                <span className="text-primary">
                                                                    {e?.firstName} {e?.lastName}
                                                                </span>{' '}
                                                                user?
                                                            </div>
                                                        ),
                                                        action: () => suspendUser(e.id ?? 0),
                                                    })
                                                );
                                            }}
                                            eventKey="4"
                                        >
                                            {e.isSuspended ? 'Lift suspension' : 'Suspend'}
                                        </Dropdown.Item>
                                    </DropdownButton>
                                </td>
                            </tr>
                        ))}
                        {user.length < 15 && !!user.length && (
                            <tr>
                                <td colSpan={99} style={{ height: '220px' }} />
                            </tr>
                        )}
                    </tbody>
                </Table>
            </TableLayout>
            <Modal
                show={showChangePassword}
                onHide={handleCloseModal}
                aria-labelledby="contained-modal-title-vcenter"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">Change password #{selectedUser?.id}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label className="required">New password</Form.Label>
                        <div className="d-flex align-items-center">
                            <Form.Control
                                as="input"
                                onChange={changePasswordHandler}
                                value={passState?.password ?? ''}
                                type={!isPasswordHidden ? 'password' : 'text'}
                                name="password"
                            />
                            <span
                                style={{ marginLeft: '-2rem' }}
                                className="cursor-pointer"
                                onClick={() => setIsPasswordHidden(prev => !prev)}
                            >
                                {isPasswordHidden ? <AiFillEye size={15} /> : <AiFillEyeInvisible size={15} />}
                            </span>
                        </div>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label className="required">Repeat password</Form.Label>
                        <Form.Control
                            as="input"
                            onChange={changePasswordHandler}
                            value={passState?.repeatPassword ?? ''}
                            type="password"
                            name="repeatPassword"
                        />
                    </Form.Group>
                    <PasswordVerifySteps password={passState?.password} />
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={handleCloseModal} variant="outline-primary">
                        Close
                    </Button>
                    <Button onClick={submitPasswordHandler} disabled={passState?.password?.length < 7}>
                        Change
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default UserList;
